import _, { isArray, isFunction, isRegExp, isString, isUndefined } from "lodash";
import { isEmptyVal } from '@/utils';
import { uiCategoryMap } from '@/common/hardcode';
import {
  BillingCycleEnum,
  DEFAULT_MAX_HOST,
  DEFAULT_MIN_HOST,
  EventSaasConsumptionCategory,
  EventSaasCoreConsumptionCategory,
  planTypes,
  zoomOneProMonthlyPlanCodes,
  zoomOneProYearlyPlanCodes,
  ConsumptionCategoryList,
  BASE_PLAN_SUB_TYPES,
  AUDIO_RATE_TYPES,
  SERVICE_CATEGORY_CODE,
  PayOnGoCategoryList,
} from '@/common/constants';
import { getCartInfo } from '@/common/service/default-mapper';
import { getPlanNameKey, getPlanTypeName } from '@/common/service/parse/parse-product-name';
import { isOnlineNotSupportPlan } from "@/common/service/non-standard-billing-cycle-utils";
import { PLAN_PRICE_CHANGE_UTILS } from '@/common/priceChange';

export const getCapacityByMapCode = mapCode => {
  return + mapCode.replace(/\w+_(\d+)/g, '$1');
}

// Exception Cases:
// 1. Pro Monthly with custom price is not in price increase scope as sales channel is not Online, apply price from subsciption
// 2. Pro Yearly with custom price is not in price increase scope as sales channel is not Online, apply price from subsciption
export function shouldApplyAmountFromSubscription(planCode1, planCode2, billingConfig) {
  const isZoomOneProMonthlyPlanCodes = planCode => zoomOneProMonthlyPlanCodes.includes(planCode);

  if (isZoomOneProMonthlyPlanCodes(planCode1) && isZoomOneProMonthlyPlanCodes(planCode2)) {
    return !_.get(billingConfig, 'isPriceIncreaseConsideredScense', true)
  }
  // PRO INCREASE - Pro Yearly need check if standard price is true or not as well.
  const isZoomOneProYearlyPlanCodes = planCode => zoomOneProYearlyPlanCodes.includes(planCode);

  if (isZoomOneProYearlyPlanCodes(planCode1) && isZoomOneProYearlyPlanCodes(planCode2)) {
    return !_.get(billingConfig, 'isPriceIncreaseConsideredScense', true)
  }
  return false;
}

export const parseSubscription2PlanList = (planList, subscription, billingConfig) => {
  let {
    plan_code,
    billingCycle,
    price,
    category,
    participants,
    parentCategory,
    min_host,
    max_host,
    quantity,
    cancelled,
    is_one_month,
    tollfree_apac_cancelled,
    tollfree_apac_isOnlineCancel,
    tollfree_apac_hosts,
    tollfree_callout,
    tollfree_countrys,
    tollfree_premium,
    originalPlanExpireTime,
    planExpireTime,
    planStartTime,
    has_decrease_request,
    has_same_pending_plan,
    has_downgrade_request,
    hasUndoChange,
    mapCode,
    assignedCount,
    mapPaidCode, // free trial planCode map to paid planCode
    isFreeTrial,
    // didUsedNumber, // preAssign use numbers
    isOnlineFreeTrial, // Is it an online free trial
    regionCode, // only from zoom one business plus plans (ZOOM-668904)
    ratePlans,
    isDisablePlanChangeOnline = false,
    planType: plan_type,
    isSingleUseProFreeTrial = false,
  } = subscription;

  let planIndex = _.findIndex(planList, plan => plan.plan_code === plan_code || plan.free_trial_pc === plan_code);
  if (isOnlineNotSupportPlan({ planType: getPlanTypeByCategory(category), billingCycle, category })) {
    planIndex = _.findIndex(planList, plan => plan.plan_code === plan_code && BillingCycleEnum[plan.billing_cycle] === billingCycle);
  }
  let plan;

  if(planIndex === -1) {
    let subType = "";
    const planBasicConfig = {
      typeNameKey: "",
      planNameKey: ""
    }

    // format ui category not match planmap.json category
    if (!isEmptyVal(uiCategoryMap[category])) {
      category = uiCategoryMap[category];
    }
    /*
      // PRO INCREASE
      1. Pro monthly plan codes : [zo_pro_monthly, monthly_c50]
        1-1. subscription: monthly_c50 (old plan code before price increase)
        1-2. need check if apply new plan code zo_pro_monthly with new price

      2. Pro yearly plan codes : [zo_pro_yearly, yearly_c50]
        1-1. subscription: yearly_c50 (old plan code before price increase)
        1-2. need check if apply new plan code zo_pro_yearly with new price
    */
    const codeIdx = zoomOneProMonthlyPlanCodes.indexOf(plan_code);
    let isStandardPrice = true;
    if(codeIdx !== -1) {
      const otherCode = zoomOneProMonthlyPlanCodes.find(item => item !== plan_code);
      isStandardPrice = !shouldApplyAmountFromSubscription(plan_code, otherCode, billingConfig)
    }

    const yearlyCodeIdx = zoomOneProYearlyPlanCodes.indexOf(plan_code);
    if(yearlyCodeIdx !== -1) {
      const otherYearlyCode = zoomOneProYearlyPlanCodes.find(item => item !== plan_code);
      isStandardPrice = !shouldApplyAmountFromSubscription(plan_code, otherYearlyCode, billingConfig)
    }

    const planType = getPlanTypeByCategory(category);

    if (planType === planTypes.ZOOM_UNITED) {
      subType = _.chain(category).split( "_").get([0], "").value();
    }

    if (planType === planTypes.ZOOM_MEETING) {
      if (category === BASE_PLAN_SUB_TYPES.ENT) {
        subType = BASE_PLAN_SUB_TYPES.ENT;
      }

      // meeting pro monthly/annual increase price case
      if (PLAN_PRICE_CHANGE_UTILS.isConsiderPriceChangeCase(plan_code)) {
        const p = _.find(planList, (x) => [x?.plan_code, x?.priceChangeMappingPlanCode].includes(plan_code));
        planBasicConfig.typeNameKey = p?.typeNameKey;
        planBasicConfig.planNameKey = p?.planNameKey;
      }
    }

    plan = {
      type: planType,
      plan_code: plan_code,
      ...(plan_type ? { plan_type } : {}),
      billing_cycle: BillingCycleEnum[billingCycle],
      cycleValue: billingCycle,
      isOneTime: category === SERVICE_CATEGORY_CODE.esmwbn || category === SERVICE_CATEGORY_CODE.wbnsu,
      amount: price,
      category: category,
      participants: participants,
      parent_category: parentCategory,
      min: min_host || DEFAULT_MIN_HOST,
      max: max_host || DEFAULT_MAX_HOST,
      cartInfo: getCartInfo(),
      typeNameKey: planBasicConfig.typeNameKey || getPlanTypeName(planType, subType),
      planNameKey: planBasicConfig.planNameKey || getPlanNameKey({type: planType, category}),
      isStandardPrice,
      isPPA: ConsumptionCategoryList.includes(plan_code),
      isPayongo: PayOnGoCategoryList.includes(plan_code),
      ...(planType === planTypes.ZOOM_MEETING ? { zoomMeetingCategory: category } : {}),
      ...(planType === planTypes.ZOOM_ONE_BUNDLE ? { zoomMeetingCategory: BASE_PLAN_SUB_TYPES.ZOOM_ONE_BIZ_PLUS } : {}),
      ...(planType === planTypes.ZOOM_UNITED ? { zoomMeetingCategory: /biz_\w+/.test(category) ? BASE_PLAN_SUB_TYPES.BIZ : BASE_PLAN_SUB_TYPES.PRO } : {}),
    };

    if(
      plan.type === planTypes.ZOOM_EVENTS ||
      plan.type === planTypes.EVENT_SAAS ||
      plan.type === planTypes.EVENT_SAAS_CORE ||
      plan.type === planTypes.WEBINAR ||
      plan.type === planTypes.LARGE_MEETING ||
      plan.type === planTypes.WEBINAR_PROMO
    ) {
      plan.capacity = getCapacityByMapCode(mapCode);
      plan.participants = plan.participants ?? plan.capacity

      if (plan.type === planTypes.WEBINAR) {
        plan.category = 'webinar' + plan.capacity;
        plan.sub_type = plan.category;
      } else if (plan.type === planTypes.LARGE_MEETING) {
        plan.category = 'large' + plan.capacity;
      } else if ([planTypes.EVENT_SAAS, planTypes.EVENT_SAAS_CORE].includes(plan.type)) {
        plan.category = plan.type + plan.participants;
      }
    }
  } else {
    plan = _.cloneDeep(planList[planIndex]);
  }

  plan.cartInfo.assignedCount = assignedCount;
  // free trial plan map to paid Plan code
  plan.mapPaidPlanCode = mapPaidCode;
  // Is it an online free trial
  plan.isOnlineFreeTrial = isOnlineFreeTrial;
  plan.isFreeTrial = isFreeTrial;
  plan.isSingleUseProFreeTrial = isSingleUseProFreeTrial;

  plan.ratePlans = ratePlans || [];
  plan.isDisablePlanChangeOnline = isDisablePlanChangeOnline;

  if (regionCode) {
    plan.region = regionCode;
    plan.regionCode = regionCode;
  }

  if(quantity) {
    plan.cartInfo.selected_number = quantity;
    plan.cartInfo.add_time = new Date().getTime();
    cancelled && (plan.cartInfo.cancelled = !!cancelled);
    cancelled && (plan.cancelled = cancelled);
    originalPlanExpireTime && (plan.cartInfo.originalPlanExpireTime = originalPlanExpireTime);
    is_one_month && (plan.cartInfo.is_one_month_selected = is_one_month);
    !isUndefined(tollfree_apac_hosts) && (plan.cartInfo.tollfree_apac_hosts = tollfree_apac_hosts);
    !isUndefined(tollfree_apac_cancelled) && (plan.cartInfo.tollfree_apac_cancelled = tollfree_apac_cancelled);
    !isUndefined(tollfree_apac_isOnlineCancel) && (plan.cartInfo.tollfree_apac_isOnlineCancel = tollfree_apac_isOnlineCancel);
    planExpireTime && (plan.cartInfo.planExpireTime = planExpireTime);
    planStartTime && (plan.cartInfo.planStartTime = planStartTime);
    has_decrease_request && (plan.cartInfo.has_decrease_request = has_decrease_request);
    has_same_pending_plan && (plan.cartInfo.has_same_pending_plan = has_same_pending_plan);
    has_downgrade_request && (plan.cartInfo.has_downgrade_request = has_downgrade_request);
    hasUndoChange && (plan.cartInfo.hasUndoChange = hasUndoChange);

    if ([tollfree_countrys, tollfree_premium, tollfree_callout].some((it) => !isUndefined(it))) {
      plan.cartInfo.tollFreeProfileData = {
        [AUDIO_RATE_TYPES.TOLL_FREE]: tollfree_countrys || '',
        [AUDIO_RATE_TYPES.PREMIUM]: tollfree_premium || '',
        [AUDIO_RATE_TYPES.CALL_OUT]: tollfree_callout || '',
      };
    }
  }

  if(plan.type === planTypes.ZOOM_UNITED) {
    plan.meeting_category = plan.category.slice(0, 3);
  } else if(plan.type === planTypes.ZOOM_ONE_BUNDLE) {
    plan.meeting_category = 'biz';
  } else if(plan.type === planTypes.ZONE_PRO_PLUS) {
    plan.meeting_category = 'pro';
  } else if(plan.type === planTypes.ZONE_EDU_PLUS) {
    plan.meeting_category = 'zoneduschcmp';
  }

  // just for one time plan (event saas/event saas core ppa)
  if (
    [planTypes.EVENT_SAAS, planTypes.EVENT_SAAS_CORE].includes(plan.type) &&
    plan?.isPPA &&
    !isEmptyVal(subscription?.creditPlans)
  ) {
    plan.creditPlans = subscription?.creditPlans;
  }

  return plan;
}

export const getPlanTypeByCategory = category => {
  const categoryPlanTypeMap = {
    [planTypes.ZOOM_UNITED]: /^(zoomunited|biz_z\w+|pro_z\w+|ent_z\w+)/,
    [planTypes.WEBINAR_PROMO]: /^webinar501|webinar1001$/,
    [planTypes.ZOOM_MEETING]: [
      /^(zo(_){0,1})?pro/, // isPro
      /^hc_pro/, // isHcPro
      /^hc_biz/, // isHcBiz
      /^biz/, // isBiz
      /^(education|edu_site|zoneduschcmp|zonedu)/, // isEducation
      /^(bundle|zoent|ent)/ // isEnterprise
    ],
    [planTypes.ZOOM_ROOM_HARDWARE]: /^zrh/,
    [planTypes.ZOOM_ROOMS]: /^zr/,
    [planTypes.ROOM_CONNECTOR]: /^(rc|room_connector)/,
    [planTypes.LARGE_MEETING]: /^large/,
    [planTypes.WEBINAR]: /^webinar/,
    [planTypes.TOLLFREE]: /^tollfree/,
    [planTypes.CMR]: /^cmr/,
    [planTypes.ZOOM_PHONE_HARDWARE]: /^zph/,
    [planTypes.ZOOM_PHONE]: /(^pbx)/,
    [planTypes.ZOOM_UNITED]: /^(zoomunited|biz_z\w+|pro_z\w+|ent_z\w+)/,
    [planTypes.ZOOM_EVENTS]: /^(eventplus|zoom_events|event_plus)/,
    [planTypes.VIDEO_SDK]: planTypes.VIDEO_SDK,
    [planTypes.ZOOM_WHITEBOARD]: /^(whiteboard|zm_wbd|zwb(basic|pro|plus))/i,
    // zoom one enterprise
    [planTypes.ZOOM_ONE]: /^(zopent|zoent|zonent)/i,
    // zoom one biz plus etc...
    [planTypes.ZOOM_ONE_BUNDLE]: /^(zonebundle|zone_|zonebizp|zonentst)/i, // zoom one biz plus etc...
    [planTypes.ZOOM_HARDWARE]: /^(zoom_)?hardware/i,
    [planTypes.PAID_DEVELOPER]: /^zds/i,
    [planTypes.API]: planTypes.API,
    [planTypes.WORKSPACE_RESERVATION]: /^((zwr_)?workspace_reservation)/,
    [planTypes.LDKCMR]: /^videosdk_cmr|^ldkcmr/i,
    [planTypes.VIDEO_SDK]: /^litesdk$/i,
    [planTypes.EVENT_SAAS_CORE]: [
      /^(eventsaascore(\d+)?$|zecsu)/i, // isEventSaasCore
      new RegExp(`^${EventSaasCoreConsumptionCategory}(\\d+)?$`, 'i'), // isEventSaasCoreConsumption
    ],
    [planTypes.EVENT_SAAS]: [
      /^(eventsaas(\d+)?$|zeud)/i, // isEventSaas
      new RegExp(`^${EventSaasConsumptionCategory}(\\d+)?$`, 'i') // isEventSaasConsumption
    ],
    [planTypes.ADDITIONAL_CO_EDITOR]: /^eventsaas(core)?-additionalcoeditor$/i,
    [planTypes.ADDITIONAL_HOST]: /^eventsaas(core)?-additionalhost$/i,
    [planTypes.TRANSLATION_CAPTIONS]: /^translation_captions$/i,
    [planTypes.ZOOM_SCHEDULER]: /^(zsched|zm_sched)/i,
    [planTypes.ZONE_BASIC_PLUS]: /^zobasicp/i,
    [planTypes.ZONE_PPM]: /^zonemext/i,
    [planTypes.ZOOM_CLIPS]: /^zclips|zoom_clips/i,
    [planTypes.ZOOM_DOCS]: /^zdocs/i,
    [planTypes.VIDEO_SDK_UC]: /^zsdkuc/i,
    [planTypes.MASTER_CLASS]: /^masterclass/i,
    [planTypes.ZONE_PRO_PLUS]: /^(zoneproplus|zoneprop|zopp_)/i,
    [planTypes.ZOOM_REVENUE_ACCELERATOR]: /^zm_kiwi/i,
    // zoom one pro plus etc...
    [planTypes.CONCURRENCY_MEETING_BASIC]: /^concurren(cy|t)(_meeting)?_basic/i,
    [planTypes.CONCURRENT_MEETING_PLUS]: /^concurren(cy|t)(_meeting)?_plus|ccm/i,
    [planTypes.ZCM]: /^zoom_compliance_manager/i,
    [planTypes.ZCM_STORAGE]: /^zcm_storage/i,
    [planTypes.ZONE_EDU_PLUS]: /^(zopschcmp|zoom_one_edu_school_plus)/i,
    [planTypes.VISITOR_MANAGEMENT]: /^zvstmgt/i,
    [planTypes.CUSTOM_AVATARS]: /^custom_avatars/i,
  }

  function isMapCheckMatched(checkItem, category) {
    if (isRegExp(checkItem)) {
      return checkItem.test(category);
    } else if (isFunction(checkItem)) {
      return checkItem(category);
    } else if (isString(checkItem)) {
      return checkItem === category;
    } else {
      return false;
    }
  }
  const matchedPlanType = Object.keys(categoryPlanTypeMap).find(planType => {
    const currentMapItem = categoryPlanTypeMap[planType];
    if (isArray(currentMapItem)) {
      return currentMapItem.some(subItem => isMapCheckMatched(subItem, category));
    } else {
      return isMapCheckMatched(currentMapItem, category)
    }
  })
  return matchedPlanType || '';
};

export const getRealPlanCode = (plan = {}) => {
  const planCode = plan?.plan_code;

  if (plan?.handlerParsePlanCode && _.isFunction(plan.handlerParsePlanCode)) {
    return plan.handlerParsePlanCode(planCode);
  }

  return planCode;
}
